import React, { useState, useEffect } from "react";
import { X, Mail, Edit2, Trash2, Search, ArrowLeft, ChevronDown, ChevronUp, Users } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("adminToken");
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json", ...opts.headers },
  });
  if (!res.ok) {
    let errMsg = `Request failed (${res.status})`;
    try { errMsg = await res.text(); } catch {}
    throw new Error(errMsg);
  }
  return res.json();
}

/**
 * DrilldownPanel — slide-in panel showing detail records behind a KPI card.
 */
export default function DrilldownPanel({ title, type, period, onClose, onComposeEmail, onComposeBulkEmail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(new Set());

  // Fetch data on mount or when period changes
  useEffect(() => {
    const p = period || "year";
    (async () => {
      try {
        let result;
        if (type === "bookings" || type === "revenue" || type === "avg_value") {
          result = await apiFetch(`/api/admin/analytics/drilldown/bookings?period=${p}&status=paid`);
          result._kind = "bookings";
        } else if (type === "active_rentals") {
          result = await apiFetch(`/api/admin/analytics/drilldown/bookings?period=${p}&status=active`);
          result._kind = "bookings";
        } else if (type === "pending_returns") {
          result = await apiFetch(`/api/admin/analytics/drilldown/bookings?period=${p}&status=pending_returns`);
          result._kind = "bookings";
        } else if (type === "product_views") {
          result = await apiFetch(`/api/admin/analytics/drilldown/product-views?period=${p}`);
          result._kind = "products";
        } else if (type === "customers" || type === "registered") {
          result = await apiFetch("/api/admin/analytics/drilldown/customers?type=all");
          result._kind = "customers";
        } else if (type === "paying") {
          result = await apiFetch("/api/admin/analytics/drilldown/customers?type=paying");
          result._kind = "customers";
        } else if (type === "new_customers") {
          result = await apiFetch("/api/admin/analytics/drilldown/customers?type=new");
          result._kind = "customers";
        } else if (type === "repeat") {
          result = await apiFetch("/api/admin/analytics/drilldown/customers?type=repeat");
          result._kind = "customers";
        } else if (type === "conversion") {
          result = await apiFetch(`/api/admin/analytics/drilldown/product-views?period=${p}`);
          result._kind = "products";
        } else {
          result = await apiFetch("/api/admin/analytics/drilldown/customers?type=all");
          result._kind = "customers";
        }
        setData(result);
      } catch (e) {
        console.error("Drilldown fetch error:", e);
        setData({ error: e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [type, period]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const handleDeleteCustomer = async (email, cancelBookings) => {
    try {
      await apiFetch(`/api/admin/analytics/customers/${encodeURIComponent(email)}`, {
        method: "DELETE",
        body: JSON.stringify({ cancel_bookings: cancelBookings }),
      });
      setData(prev => ({
        ...prev,
        customers: prev.customers.filter(c => c.email !== email),
      }));
      setDeleteConfirm(null);
    } catch (e) {
      alert("Delete failed: " + e.message);
    }
  };

  const handleSaveEdit = async (original, updated) => {
    try {
      await apiFetch(`/api/admin/analytics/customers/${encodeURIComponent(original.email)}`, {
        method: "PUT",
        body: JSON.stringify(updated),
      });
      setData(prev => ({
        ...prev,
        customers: prev.customers.map(c => c.email === original.email ? { ...c, ...updated } : c),
      }));
      setEditingCustomer(null);
    } catch (e) {
      alert("Save failed: " + e.message);
    }
  };

  // Sort + filter helpers
  const sortItems = (items, field) => {
    if (!field) return items;
    return [...items].sort((a, b) => {
      const av = a[field] ?? "", bv = b[field] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  };

  const filterItems = (items, fields) => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(item => fields.some(f => String(item[f] || "").toLowerCase().includes(q)));
  };

  const toggleSelect = (email) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const toggleSelectAll = (emails) => {
    setSelected(prev => prev.size === emails.length ? new Set() : new Set(emails));
  };

  // Get all emails for current view (customers or bookings)
  const getEmails = () => {
    if (!data) return [];
    if (data._kind === "customers") return (data.customers || []).filter(c => c.email).map(c => c.email);
    if (data._kind === "bookings") return (data.bookings || []).filter(b => b.customer_email).map(b => b.customer_email);
    return [];
  };

  const handleBulkEmail = () => {
    const recipients = [];
    if (data?._kind === "customers") {
      (data.customers || []).filter(c => selected.has(c.email)).forEach(c => recipients.push({ email: c.email, name: c.name || "" }));
    } else if (data?._kind === "bookings") {
      const seen = new Set();
      (data.bookings || []).filter(b => selected.has(b.customer_email)).forEach(b => {
        if (!seen.has(b.customer_email)) { seen.add(b.customer_email); recipients.push({ email: b.customer_email, name: b.customer_name || "" }); }
      });
    }
    if (recipients.length > 0) onComposeBulkEmail?.(recipients);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="drilldown-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="drilldown-close">
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <h2 className="text-lg font-semibold text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{title}</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          {/* Bulk email bar */}
          {selected.size > 0 && (
            <div className="mt-3 flex items-center justify-between bg-[#9A7B4F]/5 border border-[#9A7B4F]/20 rounded px-3 py-2" data-testid="bulk-email-bar">
              <span className="text-xs text-[#9A7B4F] font-medium">{selected.size} selected</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setSelected(new Set())} className="text-xs text-gray-400 hover:text-gray-600" data-testid="clear-selection">Clear</button>
                <Button size="sm" onClick={handleBulkEmail} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white h-7 text-xs px-3" data-testid="bulk-email-btn">
                  <Users className="w-3 h-3 mr-1.5" /> Email {selected.size}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Search bar */}
        <div className="px-6 py-3 border-b border-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-10 text-sm" data-testid="drilldown-search" />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {loading && <p className="text-center text-gray-400 py-12">Loading...</p>}
          {data?.error && <p className="text-center text-red-400 py-12">{data.error}</p>}

          {/* CUSTOMERS VIEW */}
          {data?._kind === "customers" && !loading && (
            <CustomersTable
              customers={sortItems(filterItems(data.customers || [], ["name", "email", "phone"]), sortField)}
              onSort={handleSort}
              SortIcon={SortIcon}
              onEdit={setEditingCustomer}
              onDelete={setDeleteConfirm}
              onEmail={(c) => onComposeEmail?.(c.email, c.name)}
              selected={selected}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleSelectAll}
            />
          )}

          {/* BOOKINGS VIEW */}
          {data?._kind === "bookings" && !loading && (
            <BookingsTable
              bookings={sortItems(filterItems(data.bookings || [], ["customer_name", "customer_email", "outfit_name"]), sortField)}
              onSort={handleSort}
              SortIcon={SortIcon}
              onEmail={(b) => onComposeEmail?.(b.customer_email, b.customer_name)}
              selected={selected}
              onToggleSelect={(b) => toggleSelect(b.customer_email)}
              onToggleAll={toggleSelectAll}
            />
          )}

          {/* PRODUCTS VIEW */}
          {data?._kind === "products" && !loading && (
            <ProductsTable
              products={sortItems(filterItems(data.products || [], ["name"]), sortField)}
              onSort={handleSort}
              SortIcon={SortIcon}
            />
          )}
        </div>

        {/* Edit Customer Modal */}
        {editingCustomer && (
          <EditCustomerModal
            customer={editingCustomer}
            onSave={handleSaveEdit}
            onClose={() => setEditingCustomer(null)}
          />
        )}

        {/* Delete Confirm Modal */}
        {deleteConfirm && (
          <DeleteConfirmModal
            customer={deleteConfirm}
            onConfirm={handleDeleteCustomer}
            onClose={() => setDeleteConfirm(null)}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Sub-components ──────────────────────────── */

function CustomersTable({ customers, onSort, SortIcon, onEdit, onDelete, onEmail, selected, onToggleSelect, onToggleAll }) {
  const emails = customers.filter(c => c.email).map(c => c.email);
  const allSelected = emails.length > 0 && emails.every(e => selected.has(e));
  return (
    <div>
      <p className="text-xs text-gray-400 mb-3">{customers.length} records</p>
      <table className="w-full text-sm" data-testid="customers-table">
        <thead>
          <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wider">
            <th className="pb-2 w-8">
              <input type="checkbox" checked={allSelected} onChange={() => onToggleAll(emails)} className="rounded border-gray-300" data-testid="select-all-customers" />
            </th>
            <th className="pb-2 cursor-pointer" onClick={() => onSort("name")}>Name <SortIcon field="name" /></th>
            <th className="pb-2 cursor-pointer" onClick={() => onSort("email")}>Email <SortIcon field="email" /></th>
            <th className="pb-2 hidden sm:table-cell">Phone</th>
            <th className="pb-2 cursor-pointer hidden md:table-cell" onClick={() => onSort("total_spent")}>Spent <SortIcon field="total_spent" /></th>
            <th className="pb-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((c, i) => (
            <tr key={c.email || i} className={`border-b border-gray-50 hover:bg-gray-50/50 ${selected.has(c.email) ? 'bg-[#9A7B4F]/5' : ''}`}>
              <td className="py-3 w-8">
                {c.email && <input type="checkbox" checked={selected.has(c.email)} onChange={() => onToggleSelect(c.email)} className="rounded border-gray-300" data-testid={`select-${c.email}`} />}
              </td>
              <td className="py-3 font-medium text-[#282828]">{c.name || "—"}</td>
              <td className="py-3 text-gray-500 text-xs">{c.email || "—"}</td>
              <td className="py-3 text-gray-500 text-xs hidden sm:table-cell">{c.phone || "—"}</td>
              <td className="py-3 text-gray-500 text-xs hidden md:table-cell">{c.total_spent ? `$${c.total_spent.toFixed(0)}` : "—"}</td>
              <td className="py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  {c.email && <button onClick={() => onEmail(c)} className="p-1.5 hover:bg-[#9A7B4F]/10 rounded text-[#9A7B4F]" title="Send email" data-testid={`email-${c.email}`}><Mail className="w-3.5 h-3.5" /></button>}
                  <button onClick={() => onEdit(c)} className="p-1.5 hover:bg-blue-50 rounded text-blue-500" title="Edit" data-testid={`edit-${c.email}`}><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onDelete(c)} className="p-1.5 hover:bg-red-50 rounded text-red-400" title="Delete" data-testid={`delete-${c.email}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {customers.length === 0 && <p className="text-center text-gray-300 py-8">No records found</p>}
    </div>
  );
}

function BookingsTable({ bookings, onSort, SortIcon, onEmail, selected, onToggleSelect, onToggleAll }) {
  // Section 6B Part 2 — collapse grouped bookings into one logical row so the
  // drilldown reconciles with the headline "Bookings" count. Children are
  // aggregated for display (outfit list + product count + summed rent).
  const [expandedGroup, setExpandedGroup] = React.useState(null);
  const logicalRows = React.useMemo(() => {
    // Section 6B Part 2 — LOGICAL rows: one per unique customer per bucket.
    // (matches analytics._logical_counts on the backend). Groups collapse by
    // customer_email (Kim's two adjacent MBGs → one row); non-grouped
    // singles collapse by customer_email (Ami's Sahira+Zara → one row).
    const groupBucketByCustomer = {};   // custKey -> row
    const singleBucketByCustomer = {};  // custKey -> row
    const _emailKey = (b) => (b.customer_email || b.customer_id || b.id || "").toLowerCase();
    (bookings || []).forEach(b => {
      const gid = b.admin_booking_group_id;
      const key = _emailKey(b);
      if (gid) {
        if (!groupBucketByCustomer[key]) {
          groupBucketByCustomer[key] = {
            key: `g:${key}`, kind: "group", id: gid,
            customer_name: b.customer_name, customer_email: b.customer_email,
            outfits: [], booking_status: b.booking_status,
            rental_cost: 0, created_at: b.created_at,
            children: [], group_ids: new Set(),
          };
        }
        const bucket = groupBucketByCustomer[key];
        bucket.children.push(b);
        bucket.outfits.push(b.outfit_name);
        bucket.rental_cost += (Number(b.rental_cost) || 0);
        bucket.group_ids.add(gid);
      } else {
        if (!singleBucketByCustomer[key]) {
          singleBucketByCustomer[key] = {
            key: `s:${key}`, kind: "single", id: b.id,
            customer_name: b.customer_name, customer_email: b.customer_email,
            outfits: [], booking_status: b.booking_status,
            rental_cost: 0, created_at: b.created_at,
            children: [],
          };
        }
        const bucket = singleBucketByCustomer[key];
        bucket.children.push(b);
        bucket.outfits.push(b.outfit_name);
        bucket.rental_cost += (Number(b.rental_cost) || 0);
      }
    });
    return [
      ...Object.values(groupBucketByCustomer).map(r => ({ ...r, group_ids: Array.from(r.group_ids) })),
      ...Object.values(singleBucketByCustomer),
    ];
  }, [bookings]);

  const emails = logicalRows.filter(b => b.customer_email).map(b => b.customer_email);
  const uniqueEmails = [...new Set(emails)];
  const allSelected = uniqueEmails.length > 0 && uniqueEmails.every(e => selected.has(e));
  return (
    <div>
      <p className="text-xs text-gray-400 mb-3">
        {logicalRows.length} bookings
        <span className="ml-2 text-gray-300">
          · {logicalRows.reduce((n, r) => n + (r.kind === "group" ? r.children.length : 1), 0)} products
        </span>
      </p>
      <table className="w-full text-sm" data-testid="bookings-table">
        <thead>
          <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wider">
            <th className="pb-2 w-8">
              <input type="checkbox" checked={allSelected} onChange={() => onToggleAll(uniqueEmails)} className="rounded border-gray-300" data-testid="select-all-bookings" />
            </th>
            <th className="pb-2 cursor-pointer" onClick={() => onSort("customer_name")}>Customer <SortIcon field="customer_name" /></th>
            <th className="pb-2">Outfit</th>
            <th className="pb-2 cursor-pointer hidden sm:table-cell" onClick={() => onSort("rental_cost")}>Amount <SortIcon field="rental_cost" /></th>
            <th className="pb-2 hidden md:table-cell">Status</th>
            <th className="pb-2 cursor-pointer hidden md:table-cell" onClick={() => onSort("created_at")}>Date <SortIcon field="created_at" /></th>
            <th className="pb-2 text-right">Email</th>
          </tr>
        </thead>
        <tbody>
          {logicalRows.map((r, i) => (
            <React.Fragment key={r.key || i}>
              <tr className={`border-b border-gray-50 hover:bg-gray-50/50 ${selected.has(r.customer_email) ? 'bg-[#9A7B4F]/5' : ''}`}
                  data-testid={`drilldown-row-${r.kind}-${r.id}`}>
                <td className="py-3 w-8">
                  {r.customer_email && <input type="checkbox" checked={selected.has(r.customer_email)} onChange={() => onToggleSelect(r)} className="rounded border-gray-300" />}
                </td>
                <td className="py-3 font-medium text-[#282828]">
                  {r.kind === "group" && (
                    <button type="button" onClick={() => setExpandedGroup(v => v === r.id ? null : r.id)}
                            className="mr-1 text-[10px] text-[#9A7B4F]" data-testid={`drilldown-expand-${r.id}`}>
                      {expandedGroup === r.id ? "▼" : "▶"}
                    </button>
                  )}
                  {r.customer_name || "—"}
                </td>
                <td className="py-3 text-gray-500 text-xs truncate max-w-[200px]">
                  {r.kind === "group" ? (
                    <span>
                      <span className="text-[9px] px-1.5 py-0.5 bg-[#9A7B4F]/10 text-[#9A7B4F] mr-1 uppercase tracking-wider">Group</span>
                      {r.outfits.slice(0, 2).join(", ")}{r.outfits.length > 2 ? ` +${r.outfits.length - 2}` : ""}
                      <span className="text-gray-400 ml-1">· {r.children.length} products</span>
                    </span>
                  ) : (r.outfit_name || "—")}
                </td>
                <td className="py-3 text-gray-500 text-xs hidden sm:table-cell">${(Number(r.rental_cost) || 0).toFixed(0)}</td>
                <td className="py-3 hidden md:table-cell">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.booking_status === "active" ? "bg-green-50 text-green-600" : r.booking_status === "confirmed" ? "bg-blue-50 text-blue-600" : "bg-gray-50 text-gray-500"}`}>
                    {r.booking_status || "—"}
                  </span>
                </td>
                <td className="py-3 text-gray-400 text-xs hidden md:table-cell">{r.created_at?.slice(0, 10) || "—"}</td>
                <td className="py-3 text-right">
                  {r.customer_email && <button onClick={() => onEmail(r)} className="p-1.5 hover:bg-[#9A7B4F]/10 rounded text-[#9A7B4F]" title="Send email"><Mail className="w-3.5 h-3.5" /></button>}
                </td>
              </tr>
              {r.kind === "group" && expandedGroup === r.id && r.children.map((c, j) => (
                <tr key={`${r.id}-${c.id}`} className="border-b border-gray-50 bg-[#FAF8F0]/40 text-[11px]"
                    data-testid={`drilldown-child-${c.id}`}>
                  <td></td>
                  <td className="py-2 pl-6 text-gray-500">{c.outfit_name || "—"}</td>
                  <td className="py-2 text-gray-400 text-[10px]">{c.id}</td>
                  <td className="py-2 text-gray-500 hidden sm:table-cell">${(Number(c.rental_cost) || 0).toFixed(0)}</td>
                  <td className="py-2 hidden md:table-cell">
                    <span className="text-[10px] text-gray-400">{c.booking_status || "—"}</span>
                  </td>
                  <td colSpan={2}></td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {bookings.length === 0 && <p className="text-center text-gray-300 py-8">No records found</p>}
    </div>
  );
}

function ProductsTable({ products, onSort, SortIcon }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-3">{products.length} products</p>
      <table className="w-full text-sm" data-testid="products-table">
        <thead>
          <tr className="border-b text-left text-xs text-gray-500 uppercase tracking-wider">
            <th className="pb-2">#</th>
            <th className="pb-2 cursor-pointer" onClick={() => onSort("name")}>Product <SortIcon field="name" /></th>
            <th className="pb-2 cursor-pointer text-right" onClick={() => onSort("views")}>Views <SortIcon field="views" /></th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => (
            <tr key={p.outfit_id || i} className="border-b border-gray-50 hover:bg-gray-50/50">
              <td className="py-3 text-gray-400 text-xs">{i + 1}</td>
              <td className="py-3 font-medium text-[#282828]">{p.name}</td>
              <td className="py-3 text-right text-[#9A7B4F] font-semibold">{p.views}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {products.length === 0 && <p className="text-center text-gray-300 py-8">No records found</p>}
    </div>
  );
}

function EditCustomerModal({ customer, onSave, onClose }) {
  const [form, setForm] = useState({
    name: customer.name || "",
    email: customer.email || "",
    phone: customer.phone || "",
    address: customer.address || "",
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6" data-testid="edit-customer-modal">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Edit Customer</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Name</label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-name" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Email</label>
            <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="edit-email" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Phone</label>
            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="edit-phone" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Address</label>
            <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} data-testid="edit-address" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(customer, form)} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white" data-testid="save-edit-btn">Save</Button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ customer, onConfirm, onClose }) {
  const [cancelBookings, setCancelBookings] = useState(false);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6" data-testid="delete-confirm-modal">
        <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Delete Customer</h3>
        <p className="text-sm text-gray-500 mb-4">Are you sure you want to delete <strong>{customer.name || customer.email}</strong>?</p>
        <label className="flex items-center gap-2 text-sm text-gray-600 mb-4 cursor-pointer">
          <input type="checkbox" checked={cancelBookings} onChange={e => setCancelBookings(e.target.checked)} className="rounded border-gray-300" data-testid="cancel-bookings-check" />
          Also cancel their active bookings
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Keep</Button>
          <Button onClick={() => onConfirm(customer.email, cancelBookings)} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-btn">Delete</Button>
        </div>
      </div>
    </div>
  );
}
